import React, { useState } from 'react';
import { Info, AlertTriangle, OctagonAlert } from 'lucide-react';

/**
 * Displays the marker-type scan banner above Molecular Diversity
 * results.  Surfaces inferred marker types (codominant SSR, dominant
 * RAPD/AFLP, biallelic SNP) along with the pipeline assumption that
 * was applied, so the breeder can decide whether the analysis is
 * methodologically appropriate.
 *
 * Pattern 3 (defense-in-depth warnings) + Pattern 10
 * (uncertainty-honest decision aids).
 *
 * Shape of `scan`:
 * {
 *   per_marker: [{ marker, inferred_type, evidence, assumption_applied, ... }, ...],
 *   summary: { codominant_multiallelic: N, codominant_biallelic: N, dominant: N, ambiguous: N, total: N },
 *   banner: { level, title, message, pipeline_assumption, references }
 * }
 */
export function MolecularMarkerScanBanner({ scan, testId = 'marker-type-scan' }) {
  const [showDetail, setShowDetail] = useState(false);
  if (!scan || !scan.banner) return null;
  const { banner, summary, per_marker } = scan;
  const palette = {
    info: { bg: '#E8F4ED', border: '#B7DEC6', fg: '#15803D', Icon: Info },
    warning: { bg: '#FFF6E0', border: '#E5C46B', fg: '#92400E', Icon: AlertTriangle },
    critical: { bg: '#FDE7E7', border: '#F2B5B5', fg: '#991B1B', Icon: OctagonAlert },
  };
  const p = palette[banner.level] || palette.info;
  const { Icon } = p;

  return (
    <div
      className="rounded-xl px-4 py-3 mt-4"
      style={{ background: p.bg, border: `1px solid ${p.border}` }}
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <Icon size={18} style={{ color: p.fg, flexShrink: 0, marginTop: 2 }} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold" style={{ color: p.fg }}>
            {banner.title}
          </div>
          <p className="text-xs mt-1" style={{ color: p.fg, lineHeight: 1.5 }}>
            {banner.message}
          </p>
          {banner.pipeline_assumption && (
            <p className="text-[11px] italic mt-1" style={{ color: p.fg, opacity: 0.85 }}>
              <strong>Pipeline assumption applied:</strong> {banner.pipeline_assumption}
            </p>
          )}
          {summary && (
            <div className="flex flex-wrap gap-2 mt-2 text-[10px] uppercase tracking-wider font-mono">
              <SummaryChip label="Biallelic codominant (SNP)" count={summary.codominant_biallelic} accent="#16A34A" />
              <SummaryChip label="Multi-allelic (SSR)" count={summary.codominant_multiallelic} accent="#0891B2" />
              <SummaryChip label="Dominant (RAPD/AFLP)" count={summary.dominant} accent="#D97706" />
              <SummaryChip label="Ambiguous" count={summary.ambiguous} accent="#991B1B" />
              <SummaryChip label="Total" count={summary.total} accent="#475569" emphasised />
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowDetail(!showDetail)}
            className="text-[11px] underline mt-2"
            style={{ color: p.fg }}
            data-testid={`${testId}-toggle`}
          >
            {showDetail ? 'Hide per-marker breakdown' : 'Show per-marker breakdown'}
          </button>
          {showDetail && Array.isArray(per_marker) && per_marker.length > 0 && (
            <div className="overflow-x-auto mt-2" data-testid={`${testId}-detail-table`}>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr style={{ background: p.fg, color: 'white' }}>
                    <th className="px-2 py-1.5 text-left font-semibold border" style={{ borderColor: p.border }}>Marker</th>
                    <th className="px-2 py-1.5 text-left font-semibold border" style={{ borderColor: p.border }}>Inferred type</th>
                    <th className="px-2 py-1.5 text-left font-semibold border" style={{ borderColor: p.border }}>Evidence</th>
                    <th className="px-2 py-1.5 text-center font-semibold border" style={{ borderColor: p.border }}>Unique codes</th>
                  </tr>
                </thead>
                <tbody>
                  {per_marker.slice(0, 50).map((m, idx) => (
                    <tr key={m.marker} className={idx % 2 === 0 ? 'bg-white' : ''}
                      style={{ background: idx % 2 === 0 ? 'white' : '#FFFFFF80' }}>
                      <td className="px-2 py-1 font-mono border" style={{ borderColor: p.border, color: p.fg }}>{m.marker}</td>
                      <td className="px-2 py-1 border" style={{ borderColor: p.border, color: p.fg }}>
                        <TypePill type={m.inferred_type} />
                      </td>
                      <td className="px-2 py-1 border italic" style={{ borderColor: p.border, color: p.fg }}>{m.evidence}</td>
                      <td className="px-2 py-1 text-center border font-mono" style={{ borderColor: p.border, color: p.fg }}>{m.n_unique_values}</td>
                    </tr>
                  ))}
                  {per_marker.length > 50 && (
                    <tr><td colSpan={4} className="text-center px-2 py-1 italic text-[10px]" style={{ color: p.fg }}>
                      … {per_marker.length - 50} more markers not shown.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {Array.isArray(banner.references) && banner.references.length > 0 && (
            <details className="mt-2 text-[10px]" data-testid={`${testId}-refs`}>
              <summary className="cursor-pointer" style={{ color: p.fg }}>References</summary>
              <ul className="mt-1 list-disc pl-5" style={{ color: p.fg }}>
                {banner.references.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}


function SummaryChip({ label, count, accent, emphasised }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5"
      style={{
        background: emphasised ? accent : 'white',
        color: emphasised ? 'white' : accent,
        border: `1px solid ${accent}`,
        fontWeight: 600,
      }}
    >
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</span>
      <span style={{ fontWeight: 500, opacity: 0.9 }}>{label}</span>
    </span>
  );
}


const TYPE_LABELS = {
  codominant_multiallelic: { label: 'SSR / multi-allelic', color: '#0891B2' },
  codominant_biallelic: { label: 'SNP / biallelic', color: '#16A34A' },
  dominant: { label: 'RAPD / AFLP (dominant)', color: '#D97706' },
  ambiguous: { label: 'Ambiguous', color: '#991B1B' },
};


function TypePill({ type }) {
  const meta = TYPE_LABELS[type] || { label: type, color: '#475569' };
  return (
    <span style={{
      color: meta.color, border: `1px solid ${meta.color}`,
      fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99,
      whiteSpace: 'nowrap',
    }}>{meta.label}</span>
  );
}


export default MolecularMarkerScanBanner;
